import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Grid from "../../Components/Grid/Grid";
import Modal from "../../Components/Modal/Task/TaskM";
import Button from "../../Components/button/button";
import "../../Style/ProblemView.css";
import config from "../../config";
import getColumnDefs from "../../utils/Task/taskColumnDefs";
import { TaskData } from "../../utils/Task/taskTypes";
import { formatDuration } from "../../utils/durationUtils";
import { GridApi } from "ag-grid-community";
import { authFetch } from "../../utils/Security/authFetch";
import localforage from "localforage";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../../Style/TaskCalendar.css";

interface PersistedState {
  searchQuery: string;
  isCalendarView: boolean;
  selectedTaskId: string | null;
  isModalOpen: boolean;
  gridFilterModel: any;
  gridSortModel: any;
}
interface Page<T> {
  content: T[];
  pageable: any;
  totalElements: number;
  totalPages: number;
  last: boolean;
  number: number;
  size: number;
  first: boolean;
  numberOfElements: number;
  sort: any;
  empty: boolean;
}
function TaskView() {
  const username = localStorage.getItem("username") || "System";
  const userTeam = localStorage.getItem("position");
  const userId = localStorage.getItem("userid");
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const PERSISTENCE_KEY = `taskView_state_${isAdmin ? "admin" : userId}`;
  const INCIDENT_CACHE_KEY = `incidents_${isAdmin ? "admin" : userId}`;
  const isForTelebras = localStorage.getItem("isForTelebras") === "true";
 const isForPetrobras = localStorage.getItem("isForPetrobras") === "true";
  const isForNilesat = localStorage.getItem("isForNilesat") === "true";
  const [rowData, setRowData] = useState<TaskData[]>([]);
  const [filteredData, setFilteredData] = useState<TaskData[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCalendarView, setIsCalendarView] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  // Petrobras admins see a different list, so they get their own cache
  const CACHE_KEY = `tasks_${
    isAdmin ? (isForPetrobras ? "admin_petrobras" : "admin") : userId
  }`;

  // ✅ Petrobras admins only see Petrobras tasks
  const applyPetrobrasScope = (rows: TaskData[]): TaskData[] =>
    isAdmin && isForPetrobras
      ? rows.filter((item: any) =>
          String(item.affectedCustomer ?? "").includes("Petrobras")
        )
      : rows;

  const navigate = useNavigate();
  const location = useLocation();
  const gridApiRef = useRef<GridApi | null>(null);

  // ------------------- Persistence Helpers -------------------
  const saveGridStateToStorage = () => {
    if (!gridApiRef.current) return;

    const filterModel = gridApiRef.current.getFilterModel();
    const sortModel = (gridApiRef.current.getColumnState() ?? [])
      .filter((col) => col.sort != null)
      .map((col) => ({
        colId: col.colId,
        sort: col.sort,
        sortIndex: col.sortIndex,
      }));

    const stateToSave: PersistedState = {
      searchQuery,
      isCalendarView,
      selectedTaskId: selectedTask?.ticketId || null,
      isModalOpen,
      gridFilterModel: filterModel,
      gridSortModel: sortModel,
    };

    localStorage.setItem(PERSISTENCE_KEY, JSON.stringify(stateToSave));
  };
  const loadMyTodo = async () => {
    if (!username) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/tasks/my-todo/${username}`
      );
      const data: TaskData[] = applyPetrobrasScope(await res.json());

      setRowData(data);
      setFilteredData(data);
    } catch (e) {
      console.error("Failed to load My Todo", e);
    } finally {
      setLoading(false);
    }
  };
  // ✅ RESET TO FULL LIST (from cache or API)
  const handleReset = async () => {
    setLoading(true);
    setSearchQuery("");

    try {
      // 1️⃣ Try restoring from cache first
      const cached = await localforage.getItem<{
        rows: TaskData[];
        ts: number;
      }>(CACHE_KEY);

      if (cached?.rows?.length) {
        setRowData(cached.rows);
        setFilteredData(cached.rows);
      } else {
        // 2️⃣ If cache is empty, force reload from API
        window.location.reload();
      }
    } catch (e) {
      console.error("Reset failed, reloading page", e);
      window.location.reload();
    } finally {
      setLoading(false);
    }
  };

  // ✅ MY GROUP TODO
  const loadMyGroupTodo = async () => {
    if (!userTeam) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/tasks/my-group-todo/${userTeam}`
      );
      const data: TaskData[] = applyPetrobrasScope(await res.json());

      setRowData(data);
      setFilteredData(data);
    } catch (e) {
      console.error("Failed to load Group Todo", e);
    } finally {
      setLoading(false);
    }
  };

  const loadPersistedState = (): string | null => {
    try {
      const savedState = localStorage.getItem(PERSISTENCE_KEY);
      if (savedState) {
        const parsed: PersistedState = JSON.parse(savedState);
        setSearchQuery(parsed.searchQuery || "");
        setIsCalendarView(parsed.isCalendarView || false);
        setIsModalOpen(parsed.isModalOpen || false);
        return parsed.selectedTaskId;
      }
    } catch (error) {
      console.error("Error loading persisted state:", error);
    }
    return null;
  };

  // ------------------- Grid Handlers -------------------
  const handleGridReady = (params: { api: GridApi }) => {
    gridApiRef.current = params.api;

    // Restore persisted grid filter/sort
    const savedState = localStorage.getItem(PERSISTENCE_KEY);
    if (savedState) {
      try {
        const parsed: PersistedState = JSON.parse(savedState);
        if (parsed.gridFilterModel)
          params.api.setFilterModel(parsed.gridFilterModel);
        if (parsed.gridSortModel?.length) {
          const columnState = params.api.getColumnState() ?? [];
          parsed.gridSortModel.forEach((sort: any) => {
            const col = columnState.find((c) => c.colId === sort.colId);
            if (col) {
              col.sort = sort.sort;
              col.sortIndex = sort.sortIndex;
            }
          });
          params.api.applyColumnState({ state: columnState });
        }
      } catch (error) {
        console.error("Error restoring grid state:", error);
      }
    }

    // ----------------- NEW: Ensure Set Filter dropdowns restore correctly -----------------
    params.api.addEventListener("firstDataRendered", () => {
      if (savedState) {
        try {
          const parsed: PersistedState = JSON.parse(savedState);
          if (parsed.gridFilterModel) {
            // Apply again after data is rendered to make sure set filters work
            params.api.setFilterModel(parsed.gridFilterModel);
          }
        } catch (error) {
          console.error(
            "Error restoring filter model on firstDataRendered:",
            error
          );
        }
      }
    });

    // Persist changes immediately
    const saveListener = () => saveGridStateToStorage();
    gridApiRef.current.addEventListener("filterChanged", saveListener);
    gridApiRef.current.addEventListener("sortChanged", saveListener);

    // Cleanup listeners
    return () => {
      gridApiRef.current?.removeEventListener("filterChanged", saveListener);
      gridApiRef.current?.removeEventListener("sortChanged", saveListener);
    };
  };

  // ------------------- Load State on Navigation -------------------
  // Selected-task restore happens in the fetch effect, so this only needs to
  // run on navigation (not on every batch of rows).
  useEffect(() => {
    if (location.pathname === "/task-view") {
      loadPersistedState();
      setIsInitialized(true);
    }
  }, [location.pathname]);

  // Save state when React state changes
  useEffect(() => {
    if (isInitialized) saveGridStateToStorage();
  }, [searchQuery, isCalendarView, isModalOpen, selectedTask, isInitialized]);

  // ------------------- Fetch Tasks -------------------
  useEffect(() => {
    localforage.removeItem(INCIDENT_CACHE_KEY);

    // Early return if conditions not met
    if (isAdmin && (isForTelebras || isForNilesat)) {
      setRowData([]);
      setFilteredData([]);
      return;
    }

    let cancelled = false;
    let savedTaskId: string | null = null;
    try {
      const savedState = localStorage.getItem(PERSISTENCE_KEY);
      if (savedState) savedTaskId = JSON.parse(savedState).selectedTaskId ?? null;
    } catch {
      savedTaskId = null;
    }

    const restoreSelectedTask = (rows: TaskData[]) => {
      if (!savedTaskId) return;
      const task = rows.find((t) => t.ticketId === savedTaskId);
      if (task) {
        setSelectedTask(task);
        savedTaskId = null;
      }
    };

    // Scope first so non-Petrobras rows are never copied/formatted
    const formatBatch = (batch: TaskData[]): TaskData[] =>
      applyPetrobrasScope(batch).map((item: any) => ({
        ...item,
        taskResolutionTime: formatDuration(item.taskResolutionTime),
      }));

    const fetchJson = async (url: string) => {
      const response = await authFetch(url);
      if (!response.ok) throw new Error("Failed to fetch tasks");
      return response.json();
    };

    // Admins: /list returns light rows (no journal/communication/attachments)
    // and Petrobras filtering is done in SQL. Pages are fetched in parallel
    // and handed over in order so the grid stays newest-first.
    const fetchAdminTasks = async (onBatch: (rows: TaskData[]) => void) => {
      const BATCH_SIZE = 1000;
      const CONCURRENCY = 4;
      const customerParam = isForPetrobras ? "&customer=Petrobras" : "";
      const pageUrl = (page: number) =>
        `${config.apiBaseUrl}/api/tasks/list?offset=${page * BATCH_SIZE}&limit=${BATCH_SIZE}${customerParam}`;

      const first: Page<TaskData> = await fetchJson(pageUrl(0));
      onBatch(first.content ?? []);

      const totalPages = first.totalPages ?? 1;
      const results: (TaskData[] | undefined)[] = [];
      let nextPage = 1;
      let nextToFlush = 1;

      const flush = () => {
        while (results[nextToFlush]) {
          onBatch(results[nextToFlush]!);
          results[nextToFlush] = [];
          nextToFlush++;
        }
      };

      const worker = async () => {
        while (!cancelled && nextPage < totalPages) {
          const page = nextPage++;
          const data: Page<TaskData> = await fetchJson(pageUrl(page));
          results[page] = data.content ?? [];
          flush();
        }
      };

      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    };

    // Non-admins: /user/{id} returns a plain list (not a Page)
    const fetchUserTasks = async (onBatch: (rows: TaskData[]) => void) => {
      const data = await fetchJson(`${config.apiBaseUrl}/api/tasks/user/${userId}`);
      onBatch(Array.isArray(data) ? data : data?.content ?? []);
    };

    const load = async () => {
      // 1️⃣ Show the last loaded list instantly (if any)
      let showingCache = false;
      try {
        const cached = await localforage.getItem<{ rows: TaskData[]; ts: number }>(
          CACHE_KEY
        );
        if (!cancelled && cached?.rows?.length) {
          setRowData(cached.rows);
          restoreSelectedTask(cached.rows);
          setLoading(false);
          showingCache = true;
        }
      } catch (e) {
        console.error("Failed to read task cache", e);
      }

      // 2️⃣ Refresh from the API. Without a cache, rows stream in as they
      // arrive; with a cache, the fresh list replaces it in one go.
      const fresh: TaskData[] = [];
      const onBatch = (batch: TaskData[]) => {
        if (cancelled) return;
        const formatted = formatBatch(batch);
        if (!formatted.length) return;
        fresh.push(...formatted);
        if (!showingCache) {
          setRowData((prev) => [...prev, ...formatted]);
          restoreSelectedTask(formatted);
        }
      };

      await (isAdmin ? fetchAdminTasks(onBatch) : fetchUserTasks(onBatch));
      if (cancelled) return;

      if (showingCache) {
        setRowData(fresh);
        restoreSelectedTask(fresh);
      }
      localforage
        .setItem(CACHE_KEY, { rows: fresh, ts: Date.now() })
        .catch((e) => console.error("Failed to write task cache", e));
    };

    // Clear data before fetching
    setRowData([]);
    setFilteredData([]);
    setLoading(true);

    load()
      .catch((error) => console.error("Error fetching tasks:", error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Stop a previous run from appending into the new one
    return () => {
      cancelled = true;
    };
  }, [isAdmin, userId, isForTelebras, isForPetrobras, isForNilesat]);

  // ------------------- Filter Handling -------------------
  useEffect(() => {
    setFilteredData(
      searchQuery
        ? rowData.filter((item) => applySearchFilter(item, searchQuery))
        : rowData
    );
  }, [searchQuery, rowData]);

  const applySearchFilter = (item: TaskData, query: string = searchQuery) => {
    const lowerQuery = query.toLowerCase();
    return Object.values(item).some(
      (value) =>
        value != null && value.toString().toLowerCase().includes(lowerQuery)
    );
  };

  // ------------------- Task Handlers -------------------
  const handleRowClick = (data: TaskData) => {
    setSelectedTask(data);
    setIsModalOpen(true);
  };

  const handleEdit = (data: TaskData) => {
    navigate(`/task-edit/${data.ticketId}`, { state: { task: data } });
  };

  const handleDelete = async (ticketId: string) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    try {
      const response = await authFetch(
        `${config.apiBaseUrl}/api/tasks/${ticketId}`,
        {
          method: "DELETE",
        }
      );
      if (response.ok) {
        setRowData((prev) => prev.filter((task) => task.ticketId !== ticketId));
        localforage
          .getItem<{ rows: TaskData[]; ts: number }>(CACHE_KEY)
          .then((cached) =>
            cached?.rows
              ? localforage.setItem(CACHE_KEY, {
                  ...cached,
                  rows: cached.rows.filter((t) => t.ticketId !== ticketId),
                })
              : null
          )
          .catch(() => {});
        if (selectedTask?.ticketId === ticketId) closeModal();
      } else {
        console.error("Failed to delete task");
      }
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  // Stable column defs: rebuilding them on every render (i.e. every batch of
  // rows) makes AG Grid re-process all columns. Handlers are read via a ref.
  const handlersRef = useRef({ handleEdit, handleDelete });
  handlersRef.current = { handleEdit, handleDelete };
  const columnDefs = useMemo(
    () =>
      getColumnDefs(
        (data: TaskData) => handlersRef.current.handleEdit(data),
        (ticketId: string) => handlersRef.current.handleDelete(ticketId)
      ),
    []
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // ------------------- CSV Export -------------------
  const handleExportCSV = (filename: string) => {
    if (!gridApiRef.current) return;

    const filteredNodes: any[] = [];
    gridApiRef.current.forEachNodeAfterFilter((node) => {
      if (node.data) filteredNodes.push(node.data);
    });
    if (!filteredNodes.length) return;

    const exportableColumns = columnDefs.filter(
      (col) => !!col.field && !col.cellRenderer
    );

    const headers = exportableColumns.map((col) => col.headerName || col.field);
    const fields = exportableColumns.map((col) => col.field!);

    const sanitizeField = (value: any, field: string) => {
      if (
        typeof value === "string" &&
        /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
      ) {
        return value.replace("T", " ").split(".")[0];
      }
      if (value == null) return "";
      value = String(value)
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/[\x00-\x1F\x7F]/g, "")
        .normalize("NFC");
      return `"${value.replace(/"/g, '""').replace(/\r?\n|\r/g, " ")}"`;
    };

    const csvRows = [
      headers.join(","), // header row
      ...filteredNodes.map((row) =>
        (fields as string[])
          .map((field) =>
            sanitizeField((row as Record<string, any>)[field], field)
          )
          .join(",")
      ),
    ];

    const blob = new Blob(["\uFEFF" + csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ------------------- Render -------------------
  return (
    <>
      <div className="top">
        <div className="add">
          <div className="left-section"></div>
          <div className="right-section">
            { !isForTelebras && !isForNilesat &&(
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/task-add")}
            >
              + Add
            </Button>
           )}
            <Button
              variant="primary"
              size="md"
              onClick={() => handleExportCSV("Task")}
            >
              Export to CSV
            </Button>
            <Button variant="primary" size="md" onClick={loadMyTodo}>
              My To-Do List
            </Button>

            <Button variant="primary" size="md" onClick={loadMyGroupTodo}>
              My Group To-Do List
            </Button>
            <Button variant="primary" size="md" onClick={handleReset}>
              Reset
            </Button>
            <input
              type="text"
              placeholder="Full Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid gray",
                width: "400px",
              }}
            />
          </div>
        </div>
      </div>

      <div className="Grid" style={{ height: "82%" }}>
        <Grid<TaskData>
          rowData={filteredData}
          columnDefs={columnDefs}
          onRowClick={handleRowClick}
          onGridReady={handleGridReady}
        />
      </div>

      <Modal isOpen={isModalOpen} data={selectedTask} onClose={closeModal} />
    </>
  );
}

export default TaskView;
